import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { DateRangeFilter, ExportBar } from '../components/Filters';
import { ColFilter } from '../components/ColFilter';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function GST() {
    // Redirect to login page if DevTools or View Source shortcuts are pressed
    useEffect(() => {
      const handleKeyDown = (e) => {
        // F12
        if (e.key === 'F12') {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+Shift+I or Cmd+Opt+I
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+U or Cmd+U
        if ((e.ctrlKey || e.metaKey) && (e.key === 'U' || e.key === 'u')) {
          window.location.href = '/login';
          e.preventDefault();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
  const { can } = useAuth();

  const getSaved = (k, d) => { try { const v = sessionStorage.getItem('gst_'+k); return v!==null ? JSON.parse(v) : d; } catch { return d; } };

  const [tab,        setTab]        = useState(() => getSaved('tab', 'with'));  // 'with' | 'without'
  const [summary,    setSummary]    = useState(null);
  const [stateData,  setStateData]  = useState([]);
  const [records,    setRecords]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(() => getSaved('page', 1));
  const [search,     setSearch]     = useState(() => getSaved('search', ''));
  const [dSearch,    setDSearch]    = useState(() => getSaved('search', ''));
  const [loading,    setLoading]    = useState(true);
  const [dateFilters, setDateFilters] = useState(() => getSaved('date', { fromDate:'', toDate:'' }));

  // Column filters (client-side on current page)
  const [colState,   setColState]   = useState(() => getSaved('state', ''));
  const [colStatus,  setColStatus]  = useState(() => getSaved('status', ''));
  const [colType,    setColType]    = useState(() => getSaved('type', ''));

  useEffect(() => {
    sessionStorage.setItem('gst_tab', JSON.stringify(tab));
    sessionStorage.setItem('gst_page', JSON.stringify(page));
    sessionStorage.setItem('gst_search', JSON.stringify(search));
    sessionStorage.setItem('gst_date', JSON.stringify(dateFilters));
    sessionStorage.setItem('gst_state', JSON.stringify(colState));
    sessionStorage.setItem('gst_status', JSON.stringify(colStatus));
    sessionStorage.setItem('gst_type', JSON.stringify(colType));
  }, [tab, page, search, dateFilters, colState, colStatus, colType]);

  // PAN detail modal
  const [selectedPAN,  setSelectedPAN]  = useState(null);
  const [panRecords,   setPanRecords]   = useState([]);
  const [panLoading,   setPanLoading]   = useState(false);

  const limit = 50;

  useEffect(() => {
    Promise.all([api.gst.summary(), api.gst.byState()])
      .then(([s, st]) => { setSummary(s); setStateData(st); });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== dSearch) {
        setDSearch(search); setPage(1);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search, dSearch]);

  const buildParams = useCallback(() => {
    const p = { page, limit };
    if (dSearch) p.search = dSearch;
    if (dateFilters.fromDate) p.fromDate = dateFilters.fromDate;
    if (dateFilters.toDate)   p.toDate   = dateFilters.toDate;
    if (colState)  p.state  = colState;
    if (colStatus) p.status = colStatus;
    if (colType)   p.type   = colType;
    return p;
  }, [page, dSearch, dateFilters, colState, colStatus, colType]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = tab === 'with'
        ? await api.gst.records(buildParams())
        : await api.gst.noGst(buildParams());
      setRecords(res.rows);
      setTotal(res.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [buildParams, tab]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  // Use records directly since server is filtering now
  const filteredRows = records;

  // We reset page to 1 when column filters change
  useEffect(() => { setPage(1); }, [colState, colStatus, colType]);

  const openPAN = async (pan) => {
    setSelectedPAN(pan);
    setPanLoading(true);
    try {
      const rows = await api.gst.panDetail(pan);
      setPanRecords(rows);
    } finally {
      setPanLoading(false);
    }
  };

  const handleExport = (fmt) => {
    const p = buildParams(); delete p.page; delete p.limit; p.format = fmt;
    if (tab === 'without') p.noGst = 'true';
    api.gst.export(p);
  };

  const clearAll = () => {
    setSearch(''); setDateFilters({ fromDate:'', toDate:'' });
    setColState(''); setColStatus(''); setColType('');
  };

  const hasFilters = dSearch || dateFilters.fromDate || dateFilters.toDate || colState || colStatus || colType;

  // Use statically known/fetched options so they don't disappear on filter
  const stateOpts = summary?.states
    ? summary.states.map(s => ({ value: s, label: s }))
    : [];

  const getStatusLabel = (val) => {
    if (val === 'Active') return '✓ Active';
    if (val === 'Cancelled suo-moto') return '✕ Cancelled suo-moto';
    if (val === 'Cancelled') return '✕ Cancelled';
    if (val === 'Suspended') return '⏸ Suspended';
    return val;
  };

  const statusOpts = summary?.statuses
    ? summary.statuses.map(s => ({ value: s, label: getStatusLabel(s) }))
    : [];

  const typeOpts = summary?.types
    ? summary.types.map(t => ({ value: t, label: t }))
    : [];

  const fmt = n => n == null ? '—' : Number(n).toLocaleString('en-IN');
  const totalPages = Math.ceil(total / limit);

  // Disable right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  return (
    <div className="page" onContextMenu={handleContextMenu}>
      <div className="page-header">
        <div>
          <h1 className="page-title">GST Enrichment</h1>
          <p className="page-desc">GSTIN records enriched from GST portal against PAN data</p>
        </div>
        {hasFilters && (
          <button className="btn-clear-all" onClick={clearAll}>✕ Clear all filters</button>
        )}
      </div>

      {/* ── Summary cards ── */}
      {summary && (
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card stat-card--accent">
            <div className="stat-value">{fmt(summary.UNIQUE_PAN)}</div>
            <div className="stat-label">Unique PANs with GSTIN</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{fmt(summary.TOTAL_GSTIN)}</div>
            <div className="stat-label">Total GSTIN Records</div>
            <div className="stat-sub">Multiple GSTINs per PAN possible</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#44BBA4' }}>{fmt(summary.ACTIVE_GSTIN)}</div>
            <div className="stat-label">Active GSTINs</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#E84855' }}>{fmt(summary.INACTIVE_GSTIN)}</div>
            <div className="stat-label">Inactive / Cancelled</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{fmt(summary.STATES_COVERED)}</div>
            <div className="stat-label">States Covered</div>
          </div>
        </div>
      )}

      {/* ── Bar chart (only on 'with' tab) ── */}
      {tab === 'with' && stateData.length > 0 && (
        <div className="chart-card" style={{ marginBottom: '1.5rem' }}>
          <h3 className="chart-title">Top States by GSTIN Count</h3>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={stateData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis dataKey="STATE" type="category" width={130} tick={{ fontSize: 11 }} interval={0} />
              <Tooltip formatter={v => v.toLocaleString('en-IN')} />
              <Bar dataKey="CNT" name="GSTINs" fill="#0F4C81" radius={[0, 4, 4, 0]} barSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Tab pills — styled like MGT7A filter pills ── */}
      <div className="filter-bar">
        <button
          className={`filter-pill ${tab === 'with' ? 'active' : ''}`}
          style={tab === 'with' ? { '--pill-active-color': '#44BBA4' } : {}}
          onClick={() => { setTab('with'); setPage(1); setSearch(''); clearAll(); }}
        >
          <span className="pill-dot" style={{ background: '#44BBA4' }} />
          Has GSTIN
          {summary && <span className="pill-count">{fmt(summary.UNIQUE_PAN)}</span>}
        </button>
        <button
          className={`filter-pill ${tab === 'without' ? 'active' : ''}`}
          style={tab === 'without' ? { '--pill-active-color': '#E8C547' } : {}}
          onClick={() => { setTab('without'); setPage(1); setSearch(''); clearAll(); }}
        >
          <span className="pill-dot" style={{ background: '#E8C547' }} />
          No GSTIN (Success PANs)
          {summary?.NO_GSTIN_COUNT != null && (
            <span className="pill-count" style={{ background: '#E8C547', color: '#333' }}>
              {fmt(summary.NO_GSTIN_COUNT)}
            </span>
          )}
        </button>
      </div>

      {/* ── Search + date filter ── */}
      <div className="filter-section" style={{ marginTop: '0.75rem' }}>
        <div className="filter-row">
          <input
            className="search-input"
            placeholder={tab === 'with' ? 'Search by PAN or company name…' : 'Search by CIN, PAN or company name…'}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {dSearch && (
            <button className="search-clear" onClick={() => { setSearch(''); setDSearch(''); }}>✕</button>
          )}
        </div>
        <DateRangeFilter
          filters={dateFilters}
          onChange={f => { setDateFilters(f); setPage(1); }}
          label={tab === 'with' ? 'GST EFFECTIVE DATE' : 'EXTRACTION DATE'}
        />
      </div>

      <ExportBar onExport={handleExport} total={total} canExport={can('export')} />

      {/* ── Table ── */}
      <div className="table-card" style={{ marginTop: '12px' }}>
        {loading ? (
          <div className="table-loading"><div className="spinner" /></div>
        ) : (
          <>
            <div className="table-meta-bar">
              <span>
                {colState || colStatus || colType
                  ? <><strong>{filteredRows.length}</strong> of {fmt(total)} records (column filter active)</>
                  : <><strong>{fmt(total)}</strong> records · Page {page} of {totalPages || 1}</>}
              </span>
              {(colState || colStatus || colType) && (
                <button className="col-filter-reset"
                  onClick={() => { setColState(''); setColStatus(''); setColType(''); }}>
                  Reset column filters
                </button>
              )}
            </div>

            {/* WITH GSTIN table */}
            {tab === 'with' && (
              filteredRows.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">🔍</div>
                  <div className="empty-state-title">No GSTIN Records Found</div>
                  <div className="empty-state-desc">
                    {dSearch
                      ? `No GSTIN records match "${dSearch}".`
                      : 'No records match the current filters.'}
                  </div>
                  {hasFilters && <button className="empty-state-btn" onClick={clearAll}>Clear Filters</button>}
                </div>
              ) : (
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>PAN</th>
                        <th>GSTIN</th>
                        <th>Legal Name</th>
                        <th>Trade Name</th>
                        <th>
                          <ColFilter label="State" options={stateOpts} value={colState} onChange={setColState} width={160} />
                        </th>
                        <th>
                          <ColFilter
                            label="Status"
                            options={statusOpts}
                            value={colStatus}
                            onChange={setColStatus}
                            width={150}
                          />
                        </th>
                        <th>
                          <ColFilter label="Type" options={typeOpts} value={colType} onChange={setColType} width={200} />
                        </th>
                        <th>Effective Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map(r => (
                        <tr key={r.GSTIN} onClick={() => openPAN(r.PAN)} style={{ cursor: 'pointer' }}>
                          <td className="mono">{r.PAN}</td>
                          <td className="mono">{r.GSTIN}</td>
                          <td className="td-company">{r.LEGAL_NAME || '—'}</td>
                          <td>{r.TRADE_NAME || '—'}</td>
                          <td>{r.TABLE_STATE || '—'}</td>
                          <td>
                            <span className={r.GSTIN_STATUS === 'Active' ? 'badge-yes' : 'badge-no'}>
                              {r.GSTIN_STATUS || '—'}
                            </span>
                          </td>
                          <td>{r.TAXPAYER_TYPE || '—'}</td>
                          <td className="date-cell">
                            {r.EFFECTIVE_REG_DATE ? new Date(r.EFFECTIVE_REG_DATE).toLocaleDateString('en-IN') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* WITHOUT GSTIN table */}
            {tab === 'without' && (
              filteredRows.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">⚠️</div>
                  <div className="empty-state-title">No Records Found</div>
                  <div className="empty-state-desc">
                    {dSearch
                      ? `No records match "${dSearch}".`
                      : 'All Success PANs have GSTINs, or no records match the current filters.'}
                  </div>
                  {hasFilters && <button className="empty-state-btn" onClick={clearAll}>Clear Filters</button>}
                </div>
              ) : (
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>CIN</th>
                        <th>PAN</th>
                        <th>Company Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Extraction Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((r, i) => (
                        <tr key={`${r.CIN}-${i}`}>
                          <td className="mono">{r.CIN || '—'}</td>
                          <td className="mono">{r.PAN}</td>
                          <td className="td-company">{r.COMPANY_NAME || '—'}</td>
                          <td className="small-text">{r.EMAIL_ID || '—'}</td>
                          <td>{r.PHONE || '—'}</td>
                          <td className="date-cell">
                            {r.EXTRACTION_DATE ? new Date(r.EXTRACTION_DATE).toLocaleDateString('en-IN') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {filteredRows.length > 0 && (
              <div className="pagination">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="page-btn">← Prev</button>
                <span className="page-info">Page {page} / {totalPages || 1}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="page-btn">Next →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── PAN Detail Modal ── */}
      {selectedPAN && (
        <div className="modal-overlay" onClick={() => setSelectedPAN(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>All GSTINs — PAN: <span className="mono">{selectedPAN}</span></h2>
              <button className="modal-close" onClick={() => setSelectedPAN(null)}>✕</button>
            </div>
            <div className="modal-body">
              {panLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <div className="spinner" />
                </div>
              ) : panRecords.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <div className="empty-state-icon">📭</div>
                  <div className="empty-state-title">No GSTINs Found</div>
                  <div className="empty-state-desc">No GSTIN records found for this PAN.</div>
                </div>
              ) : panRecords.map(r => (
                <div key={r.GSTIN} className="gstin-card">
                  <div className="gstin-card-header">
                    <span className="mono gstin-num">{r.GSTIN}</span>
                    <span className={r.GSTIN_STATUS === 'Active' ? 'badge-yes' : 'badge-no'}>{r.GSTIN_STATUS}</span>
                  </div>
                  <div className="gstin-fields">
                    <div><label>Legal Name</label><span>{r.LEGAL_NAME || '—'}</span></div>
                    <div><label>Trade Name</label><span>{r.TRADE_NAME || '—'}</span></div>
                    <div><label>State</label><span>{r.TABLE_STATE || '—'}</span></div>
                    <div><label>Taxpayer Type</label><span>{r.TAXPAYER_TYPE || '—'}</span></div>
                    <div><label>Constitution</label><span>{r.CONSTITUTION_OF_BUSINESS || '—'}</span></div>
                    <div><label>Effective Date</label><span>{r.EFFECTIVE_REG_DATE ? new Date(r.EFFECTIVE_REG_DATE).toLocaleDateString('en-IN') : '—'}</span></div>
                    <div><label>Core Business</label><span>{r.NATURE_CORE_BUSINESS || '—'}</span></div>
                    <div><label>Table Status</label><span>{r.TABLE_STATUS || '—'}</span></div>
                  </div>
                  {(r.PRINCIPAL_PLACE_ADDRESS || r.ADMIN_OFFICE || r.OTHER_OFFICE) && (
                    <div className="gstin-address" style={{ marginTop: '1rem' }}>
                      {r.PRINCIPAL_PLACE_ADDRESS && <div><label>Principal Address:</label><p>{r.PRINCIPAL_PLACE_ADDRESS}</p></div>}
                      {r.ADMIN_OFFICE && <div><label>Admin Office:</label><p>{r.ADMIN_OFFICE}</p></div>}
                      {r.OTHER_OFFICE && <div><label>Other Office:</label><p>{r.OTHER_OFFICE}</p></div>}
                    </div>
                  )}
                  {(r.NATURE_BUSINESS_ACTIVITIES || r.GOODS_AND_SERVICES) && (
                    <div className="gstin-address" style={{ marginTop: '1rem', background: '#f8fafc', padding: '1rem', borderRadius: '4px' }}>
                      {r.NATURE_BUSINESS_ACTIVITIES && <div><label>Business Activities:</label><p>{r.NATURE_BUSINESS_ACTIVITIES}</p></div>}
                      {r.GOODS_AND_SERVICES && <div><label>Goods & Services:</label><p>{r.GOODS_AND_SERVICES}</p></div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
